import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Menu, Avatar, Button } from 'antd';
import {
    HomeOutlined,
    CalendarOutlined,
    UserOutlined,
    DollarOutlined,
    BarChartOutlined,
    LogoutOutlined,
    AppstoreOutlined,
    TrophyOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    UnorderedListOutlined,
    SaveOutlined,
    HeartOutlined,
    YoutubeOutlined,
    FireOutlined,
    GiftOutlined,
    FormOutlined,
    MessageOutlined,
    RobotOutlined
} from '@ant-design/icons';
import { MdOutlinePets } from "react-icons/md";
import { fetchUser } from '../apis/usersApi';
import { jwtDecode } from 'jwt-decode';
import Features from '../enums/features';
import './navbar.css';
import NotificationBell from './notifications/NotificationBell.js';

const { Sider, Header } = Layout;

const Navbar = () => {
    const faviconEnv = process.env.REACT_APP_FAVICON || '';
    const [collapsed, setCollapsed] = useState(true); // State to control sidebar visibility
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const sidebarRef = useRef(null);
    const toggleButtonRef = useRef(null);

    // Features:
    const [isLoadPlanEnabled, setIsLoadPlanEnabled] = useState(false);
    const [isGlobalAccountingEnabled, setIsGlobalAccountingEnabled] = useState(false);
    const [isRegisterTransactionEnabled, setIsRegisterTransactionEnabled] = useState(false);
    const [isAccountingCloseEnabled, setIsAccountingCloseEnabled] = useState(false);
    const [isAccountingDetailsEnabled, setIsAccountingDetailsEnabled] = useState(false);
    const [isSalesViewEnabled, setIsSalesViewEnabled] = useState(false);
    const [isStatisticsViewEnabled, setIsStatisticsViewEnabled] = useState(false);
    const [isUsersViewEnabled, setIsUsersViewEnabled] = useState(false);
    const [isAchievementsEnabled, setIsAchievementsEnabled] = useState(false);
    const [isAgreementsEnabled, setIsAgreementsEnabled] = useState(false);
    const [isRankingEnabled, setIsRankingEnabled] = useState(false);
    const [isVirtualTrainingEnabled, setIsVirtualTrainingEnabled] = useState(false);
    const [isTamagotchiEnabled, setIsTamagotchiEnabled] = useState(false);
    const [isCourtesiesViewEnabled, setIsCourtesiesViewEnabled] = useState(false);
    const [isSeePCCEnabled, setIsSeePCCEnabled] = useState(false);
    const [isPCCEnabled, setIsPCCEnabled] = useState(false);
    const [isLifeTimeValueEnabled, setIsLifeTimeValueEnabled] = useState(false);
    const [isRetentionEnabled, setIsRetentionEnabled] = useState(false);
    const [isMembershipStatsEnabled, setIsMembershipStatsEnabled] = useState(false);
    const [isCollectPaymentEnabled, setIsCollectPaymentEnabled] = useState(false);
    const [isCourtesiesStatsEnabled, setIsCourtesiesStatsEnabled] = useState(false);
    const [isCRMEnabled, setIsCRMEnabled] = useState(false)
    const [isInternalChatbotEnabled, setIsInternalChatbotEnabled] = useState(false)
    const [isEventsCrudEnabled, setIsEventsCrudEnabled] = useState(false);
    const [isHighlightCrudEnabled, setIsHighlightCrudEnabled] = useState(false);

    useEffect(() => {
        function checkFeatures() {
            const features = JSON.parse(localStorage.getItem('user_features') || '{}');
    
            setIsLoadPlanEnabled(features[Features.LOAD_PLAN] || false);
            setIsGlobalAccountingEnabled(features[Features.GLOBAL_ACCOUNTING] || false);
            setIsRegisterTransactionEnabled(features[Features.REGISTER_TRANSACTION] || false);
            setIsAccountingCloseEnabled(features[Features.ACCOUNTING_CLOSE] || false);
            setIsAccountingDetailsEnabled(features[Features.ACCOUNTING_DETAILS] || false);
            setIsSalesViewEnabled(features[Features.SALES_VIEW] || false);
            setIsStatisticsViewEnabled(features[Features.STATISTICS_VIEW] || false);
            setIsUsersViewEnabled(features[Features.USERS_VIEW] || false);
            setIsAchievementsEnabled(features[Features.ACHIEVEMENTS] || false);
            setIsAgreementsEnabled(features[Features.AGREEMENTS] || false);
            setIsRankingEnabled(features[Features.RANKING] || false);
            setIsVirtualTrainingEnabled(features[Features.VIRTUAL_TRAININGS] || false);
            setIsTamagotchiEnabled(features[Features.TAMAGOTCHI] || false);
            setIsCourtesiesViewEnabled(features[Features.COURTESIES_VIEW] || false);
            setIsPCCEnabled(features[Features.PCC] || false);
            setIsSeePCCEnabled(features[Features.SEE_PCC] || false);
            setIsLifeTimeValueEnabled(features[Features.LIFE_TIME_VALUE] || false);
            setIsRetentionEnabled(features[Features.RETENTION] || false);
            setIsMembershipStatsEnabled(features[Features.MEMBERSHIP_STATS] || false);
            setIsCollectPaymentEnabled(features[Features.COLLECT_PAYMENT] || false);
            setIsCourtesiesStatsEnabled(features[Features.COURTESIES_STATS] || false);
            setIsCRMEnabled(features[Features.CRM] || false);
            setIsInternalChatbotEnabled(features[Features.INTERNAL_CHATBOT] || false);
            setIsEventsCrudEnabled(features[Features.EVENTS_CRUD] || false);
            setIsHighlightCrudEnabled(features[Features.HIGHLIGHT_CRUD] || false);
        }
        
        async function fetchUserData() {
            const access_token = localStorage.getItem('access_token');
            const decodedToken = jwtDecode(access_token);
            const userId = decodedToken.sub;
            setUser(await fetchUser(userId))
            setIsAuthenticated(true);
        }
        checkFeatures();
        fetchUserData();
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = "/login";
    };

    const toggleSidebar = () => {
        setCollapsed(!collapsed);
    };

    const handleClickOutside = (event) => {
        if(toggleButtonRef.current && toggleButtonRef.current.contains(event.target)) {
            return; // Ignore clicks on the toggle button itself
        }
        if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
            setCollapsed(true); // Collapse the sidebar if the click is outside
        }
    };

    const menuItems = [
        {
            key: 'home',
            icon: <HomeOutlined />,
            label: <Link to="/home">Home</Link>,
        },
        (isEventsCrudEnabled || isHighlightCrudEnabled) && {
            key: 'events_menu',
            icon: <CalendarOutlined />,
            label: 'Eventos',
            children: [
                isEventsCrudEnabled && {
                    key: 'create_event',
                    label: <Link to="/events/new">Crear evento</Link>,
                },
                isHighlightCrudEnabled && {
                    key: 'create_highlight',
                    label: <Link to="/highlight_sections/new">Destacar Imagen</Link>,
                }
            ].filter(Boolean)
        },
        {
            key: 'user',
            icon: <UserOutlined />,
            label: 'Mi Perfil',
            children: [
                isAuthenticated && {
                    key: 'my_profile',
                    icon: <UserOutlined />,
                    label: <Link to={`/users/${user?.id}`}>Mi Perfil</Link>,
                },
                isAuthenticated && isAchievementsEnabled && {
                    key: 'achievements',
                    icon: <FireOutlined />,
                    label: <Link to={`/achievements/${user?.id}`}>Logros</Link>,
                },
                !isAchievementsEnabled && isRankingEnabled && {
                    key: 'ranking',
                    icon: <TrophyOutlined />,
                    label: <Link to={`/ranking/${user?.id}`}>Ranking</Link>,
                },
                isAuthenticated && isTamagotchiEnabled && {
                    key: 'tamagotchi',
                    icon: <MdOutlinePets />,
                    label: <Link to={`/tamagotchi/${user?.id}`}>Tamagotchi</Link>,
                },
                isAgreementsEnabled && {
                    key: 'agreements',
                    icon: <HeartOutlined />,
                    label: <Link to="/agreements">Convenios</Link>,
                },
                isAuthenticated && {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: <span onClick={handleLogout}>Logout</span>,
                },
            ].filter(Boolean),
        },
        {
            key: 'plans',
            icon: <AppstoreOutlined />,
            label: 'Planes',
            children: [
                {
                    key: 'plans_list',
                    icon: <UnorderedListOutlined />,
                    label: <Link to="/plans">Planes</Link>,
                },
                isLoadPlanEnabled && {
                    key: 'plans_load',
                    icon: <SaveOutlined />,
                    label: <Link to="/planes/load">Cargar Plan</Link>,
                },
            ].filter(Boolean),
        },
        isVirtualTrainingEnabled && {
            key: '5',
            icon: <YoutubeOutlined />,
            label: <Link to="/virtual-trainings">Clases Virtuales</Link>,
        },
        isGlobalAccountingEnabled && {
            key: 'accounting',
            icon: <DollarOutlined />,
            label: 'Contaduría',
            children: [
                isRegisterTransactionEnabled && {
                    key: 'accounting_register',
                    label: <Link to="/accounting">Registrar</Link>,
                },
                isAccountingCloseEnabled && {
                    key: 'accounting_close',
                    label: <Link to="/accounting/close">Cierre Contable</Link>,
                },
                isAccountingDetailsEnabled && {
                    key: 'accounting_details',
                    label: <Link to="/accounting/details">Detalle Transacciones</Link>,
                },
                isSalesViewEnabled && {
                    key: 'sales',
                    label: <Link to="/sales">Ventas</Link>,
                },
                isCollectPaymentEnabled && {
                    key: 'collect_payment',
                    label: <Link to="/accounting/collect_payment">Realizar Cobro</Link>,
                },
            ].filter(Boolean),
        },
        isStatisticsViewEnabled && {
            key: 'statistics',
            icon: <BarChartOutlined />,
            label: 'Estadísticas',
            children: [
                isLifeTimeValueEnabled && {
                    key: 'life_time_value',
                    label: <Link to="/statistics/life_time_value">Life Time Value</Link>,
                },
                isRetentionEnabled && {
                    key: 'retention',
                    label: <Link to="/statistics/retention">Retención</Link>,
                },
                isMembershipStatsEnabled && {
                    key: 'membresías',
                    label: <Link to="/statistics/membership-distribution">Membresías</Link>,
                },
                isCourtesiesStatsEnabled && {
                    key: 'cortesías',
                    label: <Link to="/statistics/courtesies">Cortesías</Link>,
                },
            ]
        },
        isCourtesiesViewEnabled && {
            key: 'courtesies',
            icon: <GiftOutlined />,
            label: <Link to="/courtesies">Cortesías</Link>,
        },
        isUsersViewEnabled && {
            key: 'users',
            icon: <UserOutlined />,
            label: <Link to="/users">Usuarios</Link>,
        },
        isPCCEnabled && {
            key: 'pccs_container',
            icon: <FormOutlined />,
            label: 'PQRs',
            children: [
                {
                    key: 'my_pccs',
                    label: <Link to={`/my_pccs`}>Mis Solicitudes</Link>,
                },
                isSeePCCEnabled && {
                    key: 'pccs',
                    label: <Link to="/pqrs">PQRs</Link>,
                },
            ]
        },
        isCRMEnabled && {
            key: 'crm_parent',
            icon: <MessageOutlined />,
            label: 'CRM',
            children: [
                {
                    key: 'crm',
                    label: <Link to="/crm">Chat CRM</Link>,
                },
                {
                    key: 'knowledge',
                    label: <Link to="/crm/knowledge">Base de Conocimiento</Link>,
                }
            ]
        },
        isInternalChatbotEnabled && {
            key: 'chatbot',
            icon: <RobotOutlined />,
            label: <Link to="/chatbot">Chatbot</Link>,
        },
    ].filter(Boolean);

    return (
        <Layout>
            {/* Top Bar */}
            <Header
                className="w-100"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 16px',
                    background: 'None',
                }}
            >
                <div className="navbar-logo">
                    <Link to="/home">
                        <img src={faviconEnv} alt="Logo" />
                    </Link>
                </div>
                <div className='d-flex' style={{ alignItems: "center", gap: "16px" }}>
                    {isAuthenticated && <NotificationBell userId={user?.id} />}
                    <Button
                        ref={toggleButtonRef}
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={toggleSidebar}
                    />
                </div>
            </Header>

            {/* Sidebar */}
            <Sider
                trigger={null}
                ref={sidebarRef} // Attach the ref to the Sider
                collapsible
                collapsed={collapsed}
                collapsedWidth={0}
                width={250}
                style={{
                    zIndex: 1000,
                    height: '100vh',
                    position: 'fixed',
                    right: 0, // Move the sidebar to the right
                    background: '#001529',
                    overflow: 'auto',
                }}
            >
                <div className="logo" style={{ padding: '16px', textAlign: 'center' }}>
                    <img
                        src={faviconEnv}
                        alt="logo"
                        style={{ width: '40%', height: 'auto' }}
                    />
                </div>
                <Menu theme="dark" mode="inline" defaultSelectedKeys={['1']} items={menuItems} />
                {isAuthenticated && (
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <Avatar src={`/img/avatars/${user.photo}`} size="large" />
                        <p style={{ color: '#fff', marginTop: '8px' }}>Bienvenida!</p>
                    </div>
                )}
            </Sider>

            {/* Footer Navbar */}
            <div className='themed-footer'
                style={{
                    zIndex: 999,
                    position: 'fixed',
                    bottom: 0,
                    width: '100vw',
                    display: 'flex',
                    justifyContent: 'space-around',
                    padding: '10px 0',
                }}
            >
            <Link to="/home" style={{ color: '#fff', textAlign: 'center' }}>
                <HomeOutlined style={{ fontSize: '24px' }} />
            </Link>
            <Link to={`/users/${user?.id}`} style={{ color: '#fff', textAlign: 'center' }}>
                <UserOutlined style={{ fontSize: '24px' }} />
            </Link>
            {isAchievementsEnabled  ? (
                <Link to={`achievements/${user?.id}`} style={{ color: '#fff', textAlign: 'center' }}>
                    <FireOutlined style={{ fontSize: '24px' }} />
                </Link>
            )
            : isRankingEnabled && (
                <Link to={`/ranking/${user?.id}`}style={{ color: '#fff', textAlign: 'center' }}>
                    <TrophyOutlined style={{ fontSize: '24px' }} />
                </Link>
            )}
            {isTamagotchiEnabled && (
                <Link to={`/tamagotchi/${user?.id}`}style={{ color: '#fff', textAlign: 'center' }}>
                    <MdOutlinePets style={{ fontSize: '24px' }} />
                </Link>
            )}
        </div>
        </Layout>
    );
};

export default Navbar;